import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, api, patch, post } from "../api";
import type { Capture, Demo, Fact, Lane, Situation, Spec } from "../types";
import { Button, Card, CardHead, GrowingTextarea, Note, Shell, inputClass, textareaClass } from "../ui";
import { DeleteDemo } from "../DeleteDemo";
import { DraftProgress, friendlyDraftError } from "../DraftProgress";

/**
 * `inputClass` without the fixed h-10: a field that grows sets its own height. Everything
 * on this screen that holds a SENTENCE (a refusal, a lane's goal and next action, what the
 * caller says, a capture's description) uses this, because a clipped sentence is one the
 * salesperson cannot check before it is spoken to a prospect.
 */
/* `MAX_SITUATIONS` in `src/domain/spec.ts`, written out here rather than imported - the
   same way the lanes card writes out 4. The web bundle takes only TYPES from the domain,
   because importing a value from it would pull zod into the browser for one number. The
   schema is what enforces it; this only decides when the button greys out. */
const MAX_SITUATIONS = 3;
const MAX_MESSAGES = 3;

const growClass =
  "px-3 py-2 text-[14px] leading-snug border border-field rounded-lg bg-paper text-ink focus:outline-none focus:border-ink";

const CAPTURE_TYPES = ["string", "phone", "enum", "bool", "date"] as const;
const FACT_KINDS = ["hours", "area", "service", "price", "policy", "other"] as const;

/*
 * THE STORED VALUE AND THE WORD ON SCREEN ARE NOT THE SAME THING. Everything below is
 * display only - the spec still holds "enum", "hours" and "commercial_real_estate_services",
 * because that is what the agent and both validators read. This screen is used by
 * salespeople who never see the schema, so it says "Pick from a list" and "Opening hours"
 * instead.
 */
const CAPTURE_TYPE_NAMES: Record<(typeof CAPTURE_TYPES)[number], string> = {
  string: "Text",
  phone: "Phone number",
  enum: "Pick from a list",
  bool: "Yes or no",
  date: "Date",
};

const FACT_KIND_NAMES: Record<(typeof FACT_KINDS)[number], string> = {
  hours: "Opening hours",
  area: "Area covered",
  service: "Service",
  price: "Price",
  policy: "Policy",
  other: "Other",
};

/** `commercial_real_estate_services` -> `commercial real estate services`. */
const readableTopic = (topic: string) => topic.replace(/_/g, " ");

/**
 * The subject of a refusal, typed in plain words and stored as the slug the agent uses.
 *
 * IT IS NOT DECORATION. The topic is a value in `look_up_business_info`'s enum, and the
 * enum is how the model decides that "do you do commercial leasing?" means this entry -
 * so a refusal called `does_not_do` (which is what every hand-added one used to be
 * called, uneditably, then `does_not_do_2`) is one the model can barely find. Typing the
 * subject is the whole fix.
 *
 * `draft` is what makes it typable: the stored value is slugified on every keystroke, so
 * echoing it straight back would eat the space the moment you pressed it ("commercial "
 * -> "commercial" -> "commercialp"). While the field has focus the raw text wins; on blur
 * it falls back to the canonical slug, readably.
 */
function TopicInput({ value, onChange, className }: { value: string; onChange: (topic: string) => void; className: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className={className}
      value={draft ?? readableTopic(value)}
      placeholder="what they ask for"
      title="What a caller would ask for. The assistant matches their question against this."
      onChange={(e) => { setDraft(e.target.value); onChange(topicOf(e.target.value)); }}
      onBlur={() => setDraft(null)}
    />
  );
}

type Issue = { path: (string | number)[]; message: string };

function nextId(prefix: string, taken: string[]): string {
  for (let n = 1; ; n++) {
    const candidate = `${prefix}${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

const topicOf = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "fact";

export function ReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [demo, setDemo] = useState<Demo | null>(null);
  const [spec, setSpec] = useState<Spec | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newFact, setNewFact] = useState("");

  const load = useCallback(() => {
    api<{ demo: Demo }>(`/v1/demos/${id}`)
      .then(({ demo }) => { setDemo(demo); setSpec(demo.draft_spec); })
      .catch((err: ApiError) => setError(err.message));
  }, [id]);

  useEffect(load, [load]);

  const pending = useMemo(() => spec?.facts.filter((f) => f.status === "pending").length ?? 0, [spec]);

  function edit(update: (draft: Spec) => void) {
    setSpec((current) => {
      if (!current) return current;
      const next = structuredClone(current);
      update(next);
      return next;
    });
    setDirty(true);
  }

  async function save(): Promise<Demo | null> {
    if (!spec) return null;
    setSaving(true);
    setIssues([]);
    setError(null);
    try {
      const { demo: saved } = await patch<{ demo: Demo }>(`/v1/demos/${id}`, { draft_spec: spec });
      setDemo(saved);
      setDirty(false);
      return saved;
    } catch (err) {
      const problem = err instanceof ApiError ? err : null;
      const body = problem?.body as { detail?: Issue[] } | null;
      if (problem?.status === 422 && Array.isArray(body?.detail)) setIssues(body.detail);
      else setError(problem?.message ?? "Could not save.");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    const saved = dirty ? await save() : demo;
    if (!saved) return;
    try {
      // Publishing never mints a passcode any more - that is the Live screen's switch - so
      // there is nothing to stop and show the salesperson here.
      const result = await post<{ demo: Demo }>(`/v1/demos/${id}/publish`, {});
      setDemo(result.demo);
      navigate(`/demos/${id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not publish.");
    }
  }

  if (error && !spec) return <Shell crumb><main className="px-4 py-8 sm:p-10 text-[14px] text-warn">{error}</main></Shell>;

  /* A run owns the screen: the progress panel polls it and calls `load` back the moment the
   * row reaches `review`, from where the rest of this component takes over. A demo with a
   * run id and no spec is the same thing seen after a reload - including a run that DIED,
   * which the panel turns into a readable failure with a Try again on it. */
  if (demo && !demo.draft_spec && (demo.status === "drafting" || demo.draft_run_id)) {
    return (
      <Shell crumb title={demo.business_name || demo.website_url} eyebrow="Step 1 of 3 · Building the draft" actions={<DeleteDemo demo={demo} />}>
        <DraftProgress demoId={demo.id} onReady={load} />
      </Shell>
    );
  }

  if (!demo || !spec) {
    return (
      <Shell crumb title={demo?.business_name ?? "…"} eyebrow="Step 2 of 3 · Review" actions={demo ? <DeleteDemo demo={demo} /> : undefined}>
        <main className="px-4 py-8 sm:p-10 text-[14px] text-muted">
          {demo && !demo.draft_spec
            ? demo.draft_error
              ? <span>{friendlyDraftError(demo.draft_error)} <span className="text-warn">Start it again from the New demo page.</span></span>
              : "No draft yet. Run the draft from the New demo page, or wait for it to finish."
            : "Loading…"}
        </main>
      </Shell>
    );
  }

  const ready = pending === 0;
  const business = spec.business;

  return (
    <Shell
      crumb
      eyebrow="Step 2 of 3 · Review"
      title={business.name || demo.website_url}
      actions={
        <>
          {/* A line of its own on a phone, so the three buttons share the one under it. */}
          <div className="basis-full sm:basis-auto">
            {ready
              ? <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-ok-wash text-ok text-[13px] font-medium">Every fact checked</span>
              : <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-warn-wash text-warn text-[13px] font-medium">{pending} fact{pending === 1 ? "" : "s"} need your eye</span>}
          </div>
          <DeleteDemo demo={demo} />
          <Button onClick={() => void save()} disabled={!dirty || saving}>{saving ? "Saving…" : dirty ? "Save" : "Saved"}</Button>
          <Button kind="primary" disabled={!ready || saving} onClick={() => void publish()}>
            {demo.status === "live" ? "Republish" : "Publish demo"}
          </Button>
        </>
      }
    >
      {(error || issues.length > 0) && (
        <div className="mx-4 sm:mx-6 md:mx-10 mt-6 px-5 py-3 bg-warn-wash text-warn rounded-card text-[13px]">
          {error}
          {issues.map((issue, i) => <div key={i}><span className="font-mono">{issue.path.join(".")}</span>: {issue.message}</div>)}
        </div>
      )}

      <main className="flex gap-8 px-4 sm:px-6 md:px-10 py-6 sm:py-7 grow items-start flex-wrap">
        {/* `basis-[480px] grow`, NOT `w-full max-w-[768px]`. A full-width basis resolves to
            768 here, and 768 + the 32 gap + the 400 aside is wider than the row, so the
            sidebar - the prospect preview and the publish checklist - wrapped to the BOTTOM
            of a 4000px page while 300px sat empty beside the facts. A small basis that
            grows lets the two share the row and still stack on a narrow screen. */}
        <div className="flex flex-col gap-5 grow basis-[480px] min-w-0 max-w-[1000px]">
          {/* The business */}
          <Card>
            <CardHead title="The business" hint="Read from the site · edit anything wrong" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 py-4 text-[13px]">
              <Labelled label="Name"><input className={inputClass} value={business.name} onChange={(e) => edit((d) => { d.business.name = e.target.value; })} /></Labelled>
              <Labelled label="Phone the assistant answers"><input className={`${inputClass} font-mono`} value={business.phone} onChange={(e) => edit((d) => { d.business.phone = e.target.value; })} /></Labelled>
              <Labelled label="Where"><GrowingTextarea className={growClass} value={business.locality} onChange={(v) => edit((d) => { d.business.locality = v; })} /></Labelled>
              <Labelled label="Timezone"><input className={`${inputClass} font-mono`} value={business.timezone} onChange={(e) => edit((d) => { d.business.timezone = e.target.value; })} /></Labelled>
              <Labelled label="Kind of business"><input className={inputClass} value={business.kind} onChange={(e) => edit((d) => { d.business.kind = e.target.value; })} /></Labelled>
              <Labelled label="Website"><input className={`${inputClass} font-mono`} value={business.website} onChange={(e) => edit((d) => { d.business.website = e.target.value; })} /></Labelled>
            </div>
          </Card>

          {/* Facts */}
          <Card>
            <CardHead title="What it can tell callers" hint="Anything found on their website is already on. Prices and anything we could not source need your OK." />
            {/* The first column is a dropdown, and for a while it did not look like one:
                flat grey capitals that people read as a label rather than a control. It is
                headed and it is bordered now. */}
            <div className="hidden sm:flex items-center gap-4 px-5 pt-3 text-[11px] text-muted uppercase tracking-[0.06em]">
              <span className="w-[132px] shrink-0">Category</span>
              <span className="grow">What it says, and where that came from</span>
            </div>
            {spec.facts.map((fact, i) => <FactRow key={fact.id} fact={fact} onChange={(next) => edit((d) => { d.facts[i] = next; })} onRemove={() => edit((d) => { d.facts.splice(i, 1); })} />)}
            <form
              className="flex items-center gap-2 px-5 py-3 text-[13px] text-muted"
              onSubmit={(e) => {
                e.preventDefault();
                const say = newFact.trim();
                if (!say) return;
                edit((d) => {
                  const ids = d.facts.map((f) => f.id);
                  const topics = [...d.facts.map((f) => f.topic), ...d.refusals.map((r) => r.topic)];
                  let topic = topicOf(say.split(/\s+/).slice(0, 3).join(" "));
                  while (topics.includes(topic)) topic = `${topic}_2`;
                  d.facts.push({ id: nextId("f", ids), topic, kind: "other", say, source_url: "", quote: "", source_class: "inferred", status: "pending" });
                });
                setNewFact("");
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              <input className={`${inputClass} h-9`} placeholder="Add something the owner told you" value={newFact} onChange={(e) => setNewFact(e.target.value)} />
              <Button type="submit" className="h-9">Add</Button>
            </form>
          </Card>

          {/* Lanes */}
          <Card>
            <CardHead
              title="Reasons people call"
              right={
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
                  <span className="text-[12px] text-muted">Add one for each reason people ring, when what happens next is different</span>
                  <Button className="h-8 px-3 text-[13px]" disabled={spec.lanes.length >= 4} onClick={() => edit((d) => {
                    d.lanes.push({ id: nextId("lane_", d.lanes.map((l) => l.id)), label: "New reason", caller_says: "", goal: "", captures: [], next_action: "" });
                  })}>+ Add a reason</Button>
                </div>
              }
            />
            {spec.lanes.map((lane, i) => (
              <LaneEditor key={lane.id} lane={lane} onChange={(next) => edit((d) => { d.lanes[i] = next; })} onRemove={() => edit((d) => { d.lanes.splice(i, 1); })} />
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_1fr] gap-1 sm:gap-4 px-5 py-3.5 text-[13px] text-muted">
              <div className="font-medium text-ink">Anything else</div><div>Why they rang</div><div>Nothing. Polite close.</div>
            </div>
          </Card>

          {/* Situations - the other direction. Between the lanes (why people ring IN) and
              the refusals, because it reads as the other half of the same question. */}
          <Card>
            <CardHead
              title="When it rings a customer"
              right={
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
                  <span className="text-[12px] text-muted">What this business would ring somebody about, and what it might say. The prospect picks both on the demo page.</span>
                  <Button className="h-8 px-3 text-[13px]" disabled={spec.outbound.situations.length >= MAX_SITUATIONS} onClick={() => edit((d) => {
                    d.outbound.situations.push({
                      id: nextId("call_", d.outbound.situations.map((s) => s.id)),
                      label: "New reason to ring",
                      context: "",
                      messages: [{ label: "The news", text: "" }],
                    });
                  })}>+ Add a situation</Button>
                </div>
              }
            />
            {spec.outbound.situations.length === 0 ? (
              <div className="px-5 py-4 text-[13px] text-muted">
                Outbound is off for this demo — add a situation to turn it on. The demo page
                shows no “have them call you” switch until there is one.
              </div>
            ) : (
              spec.outbound.situations.map((situation, i) => (
                <SituationEditor
                  key={i}
                  situation={situation}
                  onChange={(next) => edit((d) => { d.outbound.situations[i] = next; })}
                  onRemove={() => edit((d) => { d.outbound.situations.splice(i, 1); })}
                />
              ))
            )}
          </Card>

          {/* Refusals */}
          <Card>
            <CardHead title="Things it will not offer" hint="Said out loud as a clear no, never guessed at." />
            <div className="hidden sm:flex items-center gap-3 px-5 pt-4 text-[11px] text-muted uppercase tracking-[0.06em]">
              <span className="w-[210px] shrink-0">What they ask for</span>
              <span className="grow">What it says back</span>
            </div>
            <div className="flex flex-col gap-2 px-5 py-4">
              {spec.refusals.map((refusal, i) => (
                /* On a phone: the topic and its × on one line, the answer under them. */
                <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[210px_minmax(0,1fr)_auto] gap-x-3 gap-y-2 items-start">
                  <TopicInput
                    className="w-full mt-0.5 px-3 h-9 rounded-full bg-chip border border-transparent text-[13px] hover:border-field focus:outline-none focus:border-ink focus:bg-paper"
                    value={refusal.topic}
                    onChange={(topic) => edit((d) => { d.refusals[i]!.topic = topic; })}
                  />
                  <GrowingTextarea className={`${growClass} col-span-full order-last sm:col-span-1 sm:order-none`} value={refusal.say} onChange={(say) => edit((d) => { d.refusals[i]!.say = say; })} />
                  <Button kind="quiet" className="h-10 px-2" onClick={() => edit((d) => { d.refusals.splice(i, 1); })}>×</Button>
                </div>
              ))}
              <Button className="self-start h-8 px-3 text-[13px]" onClick={() => edit((d) => {
                const topics = [...d.facts.map((f) => f.topic), ...d.refusals.map((r) => r.topic)];
                // A placeholder that reads as one, so it gets replaced. The old default
                // was `does_not_do`, which looked like a real answer and could not be
                // edited, so it stayed - and the model had to match a caller's question
                // against the words "does not do".
                let topic = "what_they_asked_for";
                while (topics.includes(topic)) topic = `${topic}_2`;
                d.refusals.push({ topic, say: `That's not something ${d.business.name || "we"} do${d.business.name ? "es" : ""}.` });
              })}>+ Say no to something</Button>
              <div className="text-[12px] text-muted">Anything you mark "Say no to it" above joins this list when you publish.</div>
            </div>
          </Card>

          {/* How it answers */}
          <Card>
            <CardHead title="How it answers" hint="The voice is the same on every demo. Only the words are theirs." />
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 px-5 py-4 items-center text-[13px]">
              <label>Assistant's name</label>
              <input className={`${inputClass} max-w-[320px]`} value={spec.assistant.name} onChange={(e) => edit((d) => { d.assistant.name = e.target.value; })} />
              <label>Opening line</label>
              <textarea rows={2} className={textareaClass} value={spec.assistant.opening_line} onChange={(e) => edit((d) => { d.assistant.opening_line = e.target.value; })} />
              <div>Languages</div>
              <div className="flex gap-2 flex-wrap">
                <span className="px-3 py-1.5 rounded-full bg-ink text-white text-[13px]">{spec.assistant.languages[0] === "en" ? "English" : spec.assistant.languages[0]}</span>
                <span className="px-3 py-1.5 rounded-full border border-field text-[13px]">+ follows the caller</span>
              </div>
              <label>Suggested first lines for the visitor</label>
              <textarea rows={4} className={textareaClass} value={spec.page.openers.join("\n")} onChange={(e) => edit((d) => { d.page.openers = e.target.value.split("\n").filter((line) => line.trim()); })} />
              <label>Brand from the site</label>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-1.5">
                  {(["primary", "primary_deep", "accent", "wash"] as const).map((key) => (
                    <label key={key} className="w-[22px] h-[22px] rounded border border-line cursor-pointer" style={{ background: spec.page.brand[key] }} title={key}>
                      <input type="color" className="opacity-0 w-0 h-0" value={spec.page.brand[key]} onChange={(e) => edit((d) => { d.page.brand[key] = e.target.value; })} />
                    </label>
                  ))}
                </div>
                <input className={`${inputClass} font-mono text-[12px] grow min-w-[220px]`} placeholder="Logo URL (optional)" value={spec.page.logo_url} onChange={(e) => edit((d) => { d.page.logo_url = e.target.value; })} />
              </div>
            </div>
          </Card>
        </div>

        {/* No mock of the demo page here any more. It restated what the fields above
            already say, and it cost the review - the part that is actually work - 400px of
            width. Its two real controls (the brand colours and the logo) moved into "How it
            answers", beside the rest of what the prospect sees. */}
        <aside className="flex flex-col gap-4 w-full max-w-[340px] shrink-0 lg:sticky lg:top-7">
          <div className="flex flex-col gap-2.5 px-5 py-4 bg-paper border border-line rounded-card">
            <div className="text-[13px] font-semibold">Before you publish</div>
            <div className="flex flex-col gap-2 text-[13px] leading-relaxed text-ink-soft">
              <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Make one test call and ask it a price and a Sunday. It should decline both.</span></div>
              <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Read the opening line out loud. It is the only scripted sentence.</span></div>
              <div className="flex gap-2.5"><span className={ready ? "text-ok" : "text-warn"}>{ready ? "✓" : "•"}</span><span>{ready ? "Every fact decided. Publish is unlocked." : `${pending} fact${pending === 1 ? "" : "s"} still undecided. Publish unlocks at zero.`}</span></div>
              <div className="flex gap-2.5"><span className={spec.outbound.situations.length ? "text-ok" : "text-muted"}>{spec.outbound.situations.length ? "✓" : "•"}</span><span>{spec.outbound.situations.length
                ? `${spec.outbound.situations.length} thing${spec.outbound.situations.length === 1 ? "" : "s"} it can ring a customer about. The notes are said in the assistant's own words — check the facts in them, not the wording.`
                : "It rings nobody. Add a situation if this business would ever ring a customer."}</span></div>
            </div>
          </div>

          <Note title="Everything here becomes the demo">
            Nothing to set up or install. The assistant reads this page the moment the prospect presses Call. If they bite, the same answers start the full build.
          </Note>
        </aside>
      </main>
    </Shell>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="flex flex-col gap-1"><span className="text-muted text-[12px]">{label}</span>{children}</div>;
}

function FactRow({ fact, onChange, onRemove }: { fact: Fact; onChange: (next: Fact) => void; onRemove: () => void }) {
  const isPending = fact.status === "pending";
  const [quoteOpen, setQuoteOpen] = useState(false);
  const priceUnsayable = fact.kind === "price" && fact.source_class !== "site";
  const hint = !fact.source_url
    ? "No page says this. Confirm it with the owner or say no to it."
    : priceUnsayable
      ? "A price from somewhere other than their own site. It cannot be confirmed - say no to it, or find it on the site."
      : fact.source_class !== "site"
        ? "Found off their site. Check it before the assistant may say it."
        : fact.kind === "price"
          ? "A price on the site. Confirm it is current before the assistant may say it."
          : null;
  const yes = "h-[34px] px-3 rounded-md text-[13px] border";
  const sel = "border-ok bg-ok-wash text-ok";
  const selNo = "border-muted bg-chip text-ink-soft";
  const plain = "border-field bg-paper text-ink";
  return (
    <div className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 px-5 py-3 border-b border-line-soft ${isPending ? "bg-warn-row" : "bg-paper"}`}>
      {/* One width, wide enough for the longest name ("Opening hours") with the native
          arrow beside it. w-auto sized each row to its own word, so a list of facts stepped
          in and out as the categories changed; the old w-[96px] was too narrow the other
          way and printed "SERVICI". */}
      <select
        title="What kind of fact this is. Price is the one that carries a rule: it may only be said when it came from their own site."
        className="w-[132px] shrink-0 text-[11px] text-muted uppercase tracking-[0.06em] bg-paper border border-field rounded-md px-2 h-7 mt-0.5 cursor-pointer hover:border-ink focus:outline-none focus:border-ink" value={fact.kind} onChange={(e) => onChange({ ...fact, kind: e.target.value as Fact["kind"] })}>
        {FACT_KINDS.map((kind) => <option key={kind} value={kind}>{FACT_KIND_NAMES[kind]}</option>)}
      </select>
      <div className="flex flex-col gap-1 grow min-w-0">
        <GrowingTextarea
          className="text-[14px] leading-snug bg-transparent border-0 border-b border-transparent focus:border-line focus:outline-none p-0"
          value={fact.say}
          onChange={(say) => onChange({ ...fact, say })}
        />
        {fact.source_url && (
          <div className="font-mono text-[12px] text-muted break-all">
            <a href={fact.source_url} target="_blank" rel="noreferrer" className="hover:text-ok">{fact.source_url.replace(/^https?:\/\//, "")}</a>
            {fact.source_class !== "site" && <span className="ml-2 text-faint">({fact.source_class.replace("_", " ")})</span>}
          </div>
        )}
        {/* The quote is EVIDENCE, so it wraps rather than truncating - but some are a whole
            paragraph of terms and conditions, which would bury the row. Two lines, and a
            click opens the rest. */}
        {fact.quote && (
          <button
            type="button"
            onClick={() => setQuoteOpen((open) => !open)}
            title={quoteOpen ? "Show less" : "Show the whole quote"}
            className={`text-[12px] text-ink-soft italic text-left cursor-pointer hover:text-ink ${quoteOpen ? "" : "line-clamp-2"}`}
          >
            “{fact.quote}”
          </button>
        )}
        {hint && <div className="text-[12px] text-warn">{hint}</div>}
      </div>
      <div className="flex gap-1.5 shrink-0 self-start">
        <button type="button" disabled={priceUnsayable} title={priceUnsayable ? "A price is sayable only when it is on their own site" : undefined}
                className={`${yes} ${fact.status === "confirmed" ? sel : plain} disabled:opacity-40`}
                onClick={() => onChange({ ...fact, status: "confirmed" })}>May say it</button>
        <button type="button" className={`${yes} ${fact.status === "refused" ? selNo : plain}`} onClick={() => onChange({ ...fact, status: "refused" })}>Say no to it</button>
        <button type="button" className={`${yes} ${plain} text-muted`} onClick={onRemove} title="Remove this fact">×</button>
      </div>
    </div>
  );
}

/* One thing the assistant would ring a customer about.
 *
 * THREE FIELDS AND NO LANE. There is no router on the way out and no record to read: the
 * message below is the WHOLE of what the assistant may say on the subject, which is why it
 * gets the room a lane's goal gets and the caption says so. A blank message is a PATCH 422
 * from the schema, shown by the issues banner at the top - the same way a blank lane label
 * is - rather than a second validator written here.
 */
function SituationEditor({ situation, onChange, onRemove }: {
  situation: Situation; onChange: (next: Situation) => void; onRemove: () => void;
}) {
  const set = <K extends keyof Situation>(key: K, value: Situation[K]) =>
    onChange({ ...situation, [key]: value });
  const setMessage = (i: number, next: Situation["messages"][number]) =>
    set("messages", situation.messages.map((m, j) => (j === i ? next : m)));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3 items-start px-5 py-4 border-b border-line-soft">
      <div className="flex flex-col gap-1">
        <Caption>What this is</Caption>
        <GrowingTextarea className={`${growClass} font-medium`} value={situation.label} onChange={(v) => set("label", v)} placeholder="Lesson reminder" />
        <input className={`${inputClass} font-mono text-[12px] h-8`} value={situation.id} onChange={(e) => set("id", e.target.value)} placeholder="short_name" />
        <Caption>Short name, used behind the scenes</Caption>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Caption>Why it is ringing them — what the customer did earlier</Caption>
          <GrowingTextarea className={growClass} value={situation.context} onChange={(v) => set("context", v)} placeholder="They have a lesson booked with us tomorrow morning." />
        </div>
        <div className="flex flex-col gap-1">
          <Caption>The news, as you would jot it down. Each one is a chip the prospect picks — write the awkward one too, that is the call a business most wants to hear handled well. The assistant says it in its own words and asks the closing question itself; every fact you write survives untouched</Caption>
          {situation.messages.map((message, i) => (
            // A GRID, not a flex row: `GrowingTextarea` hardcodes `w-full`, so a width
            // passed in `className` is two utilities of equal specificity fighting, and
            // which one wins depends on the order Tailwind happens to emit them in. A
            // grid sizes the CELL and lets `w-full` fill it. The capture rows above do
            // the same thing for the same reason.
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-start">
              {/* The chip's name beside its text from `sm` up, above it on a phone. */}
              <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-2 items-start">
                <GrowingTextarea
                  className={`${growClass} py-1.5 text-[12px] font-medium`}
                  value={message.label}
                  onChange={(label) => setMessage(i, { ...message, label })}
                  placeholder="Running late"
                />
                <GrowingTextarea
                  className={growClass}
                  value={message.text}
                  onChange={(text) => setMessage(i, { ...message, text })}
                  placeholder="Lesson's on tomorrow morning as booked, instructor picks up from the usual address"
                />
              </div>
              {/* The last one cannot go: a situation with nothing to say rings about
                  nothing, and the schema refuses it on save anyway. */}
              <Button
                kind="quiet"
                className="h-10 px-2"
                disabled={situation.messages.length <= 1}
                onClick={() => set("messages", situation.messages.filter((_, j) => j !== i))}
              >×</Button>
            </div>
          ))}
          <div>
            <Button
              className="h-8 px-3 text-[13px]"
              disabled={situation.messages.length >= MAX_MESSAGES}
              onClick={() => set("messages", [...situation.messages, { label: "", text: "" }])}
            >+ Another thing it might say</Button>
          </div>
        </div>
      </div>
      <Button kind="quiet" className="h-10 px-2 justify-self-end sm:justify-self-auto" onClick={onRemove}>×</Button>
    </div>
  );
}

/** The one-line plain-English label above a field. */
function Caption({ children }: { children: ReactNode }) {
  return <div className="text-[12px] text-muted">{children}</div>;
}

function LaneEditor({ lane, onChange, onRemove }: { lane: Lane; onChange: (next: Lane) => void; onRemove: () => void }) {
  const set = <K extends keyof Lane>(key: K, value: Lane[K]) => onChange({ ...lane, [key]: value });
  const setCapture = (i: number, next: Capture) => set("captures", lane.captures.map((c, j) => (j === i ? next : c)));
  return (
    <div className="flex flex-col gap-3 px-5 py-4 border-b border-line-soft">
      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_auto] gap-3 items-start">
        <div className="flex flex-col gap-1">
          <Caption>What they want</Caption>
          <GrowingTextarea className={`${growClass} font-medium`} value={lane.label} onChange={(v) => set("label", v)} placeholder="Book a lesson" />
          <input className={`${inputClass} font-mono text-[12px] h-8`} value={lane.id} onChange={(e) => set("id", e.target.value)} placeholder="short_name" />
          <Caption>Short name, used behind the scenes</Caption>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Caption>How a caller puts it, in their own words</Caption>
            <GrowingTextarea className={growClass} value={lane.caller_says} onChange={(v) => set("caller_says", v)} placeholder="I'd like to book a lesson." />
          </div>
          <div className="flex flex-col gap-1">
            <Caption>What the assistant does on this call</Caption>
            <GrowingTextarea className={growClass} value={lane.goal} onChange={(v) => set("goal", v)} placeholder="What it finds out, what it asks for, how it wraps up. Three sentences at most - never a line for it to read out." />
          </div>
          <div className="flex flex-col gap-1">
            <Caption>What the business does afterwards, and when</Caption>
            <GrowingTextarea className={growClass} value={lane.next_action} onChange={(v) => set("next_action", v)} placeholder="Someone rings them back within the day." />
          </div>
        </div>
        <Button kind="quiet" className="h-10 px-2 justify-self-end sm:justify-self-auto" onClick={onRemove}>×</Button>
      </div>
      <div className="flex flex-col gap-2 pl-0 sm:pl-[212px]">
        <div className="text-[12px] text-muted">What to ask the caller - their name and mobile are always taken ({lane.captures.length} of 6)</div>
        {lane.captures.map((capture, i) => (
          <div key={i} className="grid grid-cols-[minmax(0,1fr)_110px_auto] sm:grid-cols-[minmax(140px,180px)_100px_1fr_auto] gap-2 items-start">
            <input className={`${inputClass} h-8 font-mono text-[12px]`} value={capture.key} onChange={(e) => setCapture(i, { ...capture, key: e.target.value })} placeholder="field_name" />
            <select className={`${inputClass} h-8 text-[12px]`} value={capture.type} onChange={(e) => setCapture(i, { ...capture, type: e.target.value as Capture["type"], enum: e.target.value === "enum" ? capture.enum : [] })}>
              {CAPTURE_TYPES.map((t) => <option key={t} value={t}>{CAPTURE_TYPE_NAMES[t]}</option>)}
            </select>
            {/* Under the key and the type on a phone, where three columns leave it no room. */}
            <div className="flex flex-col gap-1 col-span-full order-last sm:col-span-1 sm:order-none">
              <GrowingTextarea className={`${growClass} py-1.5 text-[12px]`} value={capture.description} onChange={(v) => setCapture(i, { ...capture, description: v })} placeholder="What this is, in plain words" />
              {capture.type === "enum" && (
                <GrowingTextarea className={`${growClass} py-1.5 text-[12px] font-mono`} value={capture.enum.join(", ")} onChange={(v) => setCapture(i, { ...capture, enum: v.split(",").map((part) => part.trim()).filter(Boolean) })} placeholder="The options, separated by commas" />
              )}
            </div>
            <Button kind="quiet" className="h-8 px-2" onClick={() => set("captures", lane.captures.filter((_, j) => j !== i))}>×</Button>
          </div>
        ))}
        <Button className="self-start h-8 px-3 text-[13px]" disabled={lane.captures.length >= 6}
                onClick={() => set("captures", [...lane.captures, { key: `field_${lane.captures.length + 1}`, type: "string", enum: [], description: "" }])}>+ Ask for something else</Button>
      </div>
    </div>
  );
}
